import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import { Accordion, Alert, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';

import {
  PushNotificationCatalogDeliveryStatus,
  SendPushNotificationCatalogEntryMutation,
} from '../../../../../../__gqltypes__';
import { SolidBadge, SolidBadgeTone } from '../../../../../../utils/components/SolidBadge';
import { PUSH_NOTIFICATION_CATALOG_FOR_USER, SEND_PUSH_NOTIFICATION_CATALOG_ENTRY } from '../../../graphql';

// The catalogue query keeps its hand-written shape; the send result uses the generated type.
type CatalogEntry = {
  key: string;
  group: string;
  source: string;
  title: string;
  body: string | null;
  deeplink: string | null;
  deeplinkTarget: string | null;
  richCategory: string | null;
  settingsCategory: string | null;
  note: string | null;
};

type Catalog = {
  language: string;
  languages: Array<string>;
  environment: string;
  entries: Array<CatalogEntry>;
};

type SendResult = SendPushNotificationCatalogEntryMutation['sendPushNotificationCatalogEntry'];

type EntryOutcome = { variant: 'success' | 'warning' | 'danger'; result?: SendResult; error?: string };

type Props = {
  userId: string;
};

// A push arriving while the app is in the foreground is not shown: time to background it.
const DELAYS = [0, 5, 10, 20];

const matchesSearch = (entry: CatalogEntry, needle: string) =>
  [entry.key, entry.group, entry.title, entry.body, entry.deeplink, entry.note].some((value) =>
    value?.toLowerCase().includes(needle)
  );

const outcomeVariant = (result: SendResult): EntryOutcome['variant'] =>
  result.success > 0 && result.failure === 0 ? 'success' : 'warning';

const DELIVERY_STATUS_TONE: Record<PushNotificationCatalogDeliveryStatus, SolidBadgeTone> = {
  [PushNotificationCatalogDeliveryStatus.ACCEPTED]: 'green',
  [PushNotificationCatalogDeliveryStatus.REJECTED]: 'red',
  [PushNotificationCatalogDeliveryStatus.NOT_SENT]: 'amber',
  [PushNotificationCatalogDeliveryStatus.NO_TOKEN]: 'grey',
};

// Which server ran the send: a local backend and a deployed one can read different databases.
function SendServer({ server }: { server: SendResult['server'] }): JSX.Element {
  return (
    <div>
      Server: <b>{server.namespace ?? 'local backend (no namespace)'}</b>
      {!server.deployed && <> · not deployed</>} · Spanner <code>{server.spannerInstance ?? '?'}</code> /{' '}
      <code>{server.database}</code>
      {server.gcloudProject && (
        <>
          {' '}
          · project <code>{server.gcloudProject}</code>
        </>
      )}
    </div>
  );
}

// One row per install of the account (newest first). The push token is never sent to the admin.
// Collapsed by default (an account can have a dozen installs or more), but opened by itself
// whenever a device was rejected: then the rows are the reason anyone is looking.
function SendDeliveries({ deliveries }: { deliveries: SendResult['deliveries'] }): JSX.Element {
  const rejected = deliveries.filter(({ status }) => status === PushNotificationCatalogDeliveryStatus.REJECTED).length;
  const [open, setOpen] = useState(rejected > 0);
  // A new send of the same entry replaces `deliveries`: re-apply the rule to the new result.
  useEffect(() => setOpen(rejected > 0), [deliveries, rejected]);

  if (deliveries.length === 0) {
    return <></>;
  }
  return (
    <>
      <Button variant="link" size="sm" className="px-0" onClick={() => setOpen(!open)}>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'} mr-1`} />
        {open ? 'Hide' : 'Show'} devices ({deliveries.length}){rejected > 0 && `, ${rejected} rejected`}
      </Button>
      {open && <DeliveryTable deliveries={deliveries} />}
    </>
  );
}

function DeliveryTable({ deliveries }: { deliveries: SendResult['deliveries'] }): JSX.Element {
  return (
    <Table size="sm" bordered className="mt-1 mb-0 bg-white">
      <thead>
        <tr>
          <th>Device id</th>
          <th>Variation</th>
          <th>System</th>
          <th>Version</th>
          <th>Install created (UTC)</th>
          <th>Outcome</th>
        </tr>
      </thead>
      <tbody>
        {deliveries.map((delivery) => (
          <tr key={`${delivery.deviceId}.${delivery.variation}`}>
            <td>
              <code>{delivery.deviceId}</code>
            </td>
            <td>{delivery.variation}</td>
            <td>{delivery.systemName ?? '-'}</td>
            <td>{delivery.version ?? '-'}</td>
            <td>{delivery.created ? delivery.created.replace('T', ' ').slice(0, 19) : '-'}</td>
            <td>
              <SolidBadge tone={DELIVERY_STATUS_TONE[delivery.status]}>{delivery.status}</SolidBadge>
              {delivery.errorCode && <code className="ml-1">{delivery.errorCode}</code>}
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function SendOutcome({ outcome }: { outcome: EntryOutcome }): JSX.Element {
  if (outcome.error) {
    return <div className="text-danger small mt-1">{outcome.error}</div>;
  }
  const { result } = outcome;
  if (!result) {
    return <></>;
  }
  return (
    <Alert variant={outcome.variant} className="small mt-2 mb-0 py-2">
      {result.devices === 0 ? (
        <div>This account has no device with a push token: nothing was delivered.</div>
      ) : (
        <div>
          {result.devices} device(s): {result.success} accepted by FCM, {result.failure} rejected
          {result.delaySeconds > 0 && `, sent after ${result.delaySeconds} s`}
          {result.errors.length > 0 && <> ({result.errors.join(', ')})</>}
        </div>
      )}
      <div>
        Language: <code>{result.language}</code> · Deep link:{' '}
        {result.deeplink === null ? <i>none</i> : <code>{result.deeplink === '' ? "''" : result.deeplink}</code>}
      </div>
      {result.deeplinkNote && <div>{result.deeplinkNote}</div>}
      <SendServer server={result.server} />
      <SendDeliveries deliveries={result.deliveries} />
    </Alert>
  );
}

/**
 * QA: fires any one of the server-triggered push notifications at this account, rendered from the
 * backend's push catalogue with sample values, so its text, its variables and where its deep link
 * lands can be checked on the device.
 */
export function UserPushCatalogDisplay({ userId }: Props): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // '' = the account's own language
  const [userLang, setUserLang] = useState('');
  const [delaySeconds, setDelaySeconds] = useState(0);
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [outcomes, setOutcomes] = useState<Record<string, EntryOutcome>>({});

  const { data, loading, error } = useQuery<{ pushNotificationCatalogForUser: Catalog }>(
    PUSH_NOTIFICATION_CATALOG_FOR_USER,
    {
      variables: { input: { userId, userLang: userLang || null } },
      skip: !userId || !expanded,
      fetchPolicy: 'no-cache',
    }
  );
  const [sendEntry] = useMutation<{ sendPushNotificationCatalogEntry: SendResult }>(
    SEND_PUSH_NOTIFICATION_CATALOG_ENTRY
  );

  const catalog = data?.pushNotificationCatalogForUser;
  const needle = search.trim().toLowerCase();
  const groups = useMemo(() => {
    const byGroup = new Map<string, Array<CatalogEntry>>();
    (catalog?.entries ?? [])
      .filter((entry) => !needle || matchesSearch(entry, needle))
      .forEach((entry) => byGroup.set(entry.group, [...(byGroup.get(entry.group) ?? []), entry]));
    return Array.from(byGroup.entries());
  }, [catalog, needle]);
  const isLiveEnvironment = !!catalog && catalog.environment !== 'test';

  const toggleGroup = (group: string) =>
    setOpenGroups((previous) => {
      const next = new Set(previous);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });

  const send = (entry: CatalogEntry) => {
    if (
      isLiveEnvironment &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        `${catalog?.environment.toUpperCase()}: send "${entry.key}" to every device of this account? ` +
          'It is a real push with sample values (e.g. "Alex"), to beta and prod builds alike.'
      )
    ) {
      return;
    }
    setSendingKey(entry.key);
    sendEntry({ variables: { input: { userId, key: entry.key, userLang: userLang || null, delaySeconds } } })
      .then(({ data: response }) => {
        const result = response?.sendPushNotificationCatalogEntry;
        setOutcomes((previous) => ({
          ...previous,
          [entry.key]: result ? { variant: outcomeVariant(result), result } : { variant: 'danger', error: 'No result' },
        }));
      })
      .catch((err) =>
        setOutcomes((previous) => ({ ...previous, [entry.key]: { variant: 'danger', error: err.message } }))
      )
      .finally(() => setSendingKey(null));
  };

  return (
    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
        <Accordion>
          <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
            <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
              Push notification catalogue
              <Button variant="link">
                {!expanded && <i className="fas fa-chevron-down" />}
                {expanded && <i className="fas fa-chevron-up" />}
              </Button>
            </div>
          </Accordion.Toggle>
          <Accordion.Collapse eventKey="0">
            <>
              <div className="text-muted small mb-2">
                Sends one server-triggered push to every device of this account, with sample values, ignoring its
                notification settings. Links the app checks against live state are filled with this account&apos;s
                current event or game. The catalogue is a copy of what production sends, kept by hand: it checks the
                texts, variables and deep links, not the production sender itself.
              </div>
              {isLiveEnvironment && (
                <Alert variant="warning" className="small">
                  <b>{catalog?.environment.toUpperCase()}</b>: beta and prod share the production database and Firebase
                  project. A send is a real push to this account&apos;s devices, beta and prod builds alike. Use a QA
                  account.
                </Alert>
              )}
              <Alert variant="info" className="small py-2 mb-2">
                <b>The push will not appear while the game is open (in the foreground) on the target device.</b> Close
                or background the app first: pick a delay, tap Send, then leave the app before it fires.
              </Alert>
              <Row className="mb-2">
                <Col md={5}>
                  <Form.Control
                    size="sm"
                    placeholder="Search key, text, link…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Col>
                <Col md={4}>
                  <Form.Control size="sm" as="select" value={userLang} onChange={(e) => setUserLang(e.target.value)}>
                    <option value="">Account language{catalog && !userLang ? ` (${catalog.language})` : ''}</option>
                    {(catalog?.languages ?? []).map((language) => (
                      <option key={language} value={language}>
                        {language}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col md={3}>
                  <Form.Control
                    size="sm"
                    as="select"
                    value={delaySeconds}
                    onChange={(e) => setDelaySeconds(Number(e.target.value))}
                  >
                    {DELAYS.map((delay) => (
                      <option key={delay} value={delay}>
                        {delay === 0 ? 'Send now' : `Send after ${delay} s`}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
              </Row>
              {loading && <Spinner animation="border" size="sm" />}
              {error && <Alert variant="danger">{error.message}</Alert>}
              {catalog && groups.length === 0 && (
                <div className="text-muted small">No push matches “{search}”.</div>
              )}
              {groups.map(([group, entries]) => {
                const isOpen = !!needle || openGroups.has(group);
                return (
                  <div key={group} className="mb-1">
                    <Button variant="light" size="sm" block className="text-left" onClick={() => toggleGroup(group)}>
                      <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} mr-2`} />
                      {group}{' '}
                      <SolidBadge tone="grey">{entries.length}</SolidBadge>
                    </Button>
                    {isOpen &&
                      entries.map((entry) => (
                        <div key={entry.key} className="border-bottom py-2 px-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="mr-2">
                              <code className="small">{entry.key}</code>
                              {entry.settingsCategory && (
                                <SolidBadge tone="grey" className="ml-2">
                                  {entry.settingsCategory}
                                </SolidBadge>
                              )}
                              <div>
                                <b>{entry.title}</b>
                              </div>
                              {entry.body && <div>{entry.body}</div>}
                              <div className="small text-muted">
                                Deep link:{' '}
                                {entry.deeplink === null ? (
                                  <i>none</i>
                                ) : (
                                  <code>{entry.deeplink === '' ? "''" : entry.deeplink}</code>
                                )}
                                {entry.deeplinkTarget && <> · filled at send time with {entry.deeplinkTarget}</>}
                              </div>
                              {entry.note && <div className="small text-muted">{entry.note}</div>}
                            </div>
                            <Button
                              size="sm"
                              disabled={sendingKey !== null}
                              onClick={() => send(entry)}
                              style={{ whiteSpace: 'nowrap' }}
                            >
                              {sendingKey === entry.key ? <Spinner animation="border" size="sm" /> : 'Send'}
                            </Button>
                          </div>
                          {outcomes[entry.key] && <SendOutcome outcome={outcomes[entry.key]} />}
                        </div>
                      ))}
                  </div>
                );
              })}
            </>
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>
    </Card>
  );
}
